import { BarChart3, Hash, Percent, TrendingUp } from "lucide-react";
import { detectColumnKind, formatCompact, formatNumber } from "@/lib/format";

type Props = {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
};

/**
 * Renders compact "headline" tiles above the results table when the
 * result looks like a KPI: either a single aggregate row, or a result
 * set with obvious numeric summary columns. For everything else (lists
 * of objects, etc.) we render a single "row count" tile and let the
 * table carry the rest of the information.
 */
export function KpiTiles({ columns, rows, rowCount }: Props) {
  const numericCols: Array<{ idx: number; name: string; sum: number; max: number; min: number }> = [];
  for (let i = 0; i < columns.length; i++) {
    const kind = detectColumnKind(rows.map((r) => r[i]));
    if (kind !== "number") continue;
    let sum = 0, max = -Infinity, min = Infinity, seen = 0;
    for (const r of rows) {
      const v = r[i];
      if (v === null || v === undefined || v === "") continue;
      const n = Number(v);
      if (!Number.isFinite(n)) continue;
      sum += n;
      if (n > max) max = n;
      if (n < min) min = n;
      seen++;
    }
    if (seen > 0) numericCols.push({ idx: i, name: columns[i], sum, max, min });
  }

  // Single-row aggregate (e.g. COUNT, AVG, SUM as one row) — show each value as a tile.
  const isSingleRow = rows.length === 1 && numericCols.length > 0;

  if (isSingleRow) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4" data-testid="kpi-tiles">
        {columns.map((col, i) => (
          <Tile
            key={i}
            icon={pickIcon(col)}
            label={humanize(col)}
            value={
              typeof rows[0][i] === "number" || (typeof rows[0][i] === "string" && /^-?\d/.test(String(rows[0][i])))
                ? formatNumber(rows[0][i] as number | string)
                : String(rows[0][i] ?? "—")
            }
          />
        ))}
      </div>
    );
  }

  // Multi-row: row count + up to 2 summary stats on dominant numeric columns.
  const stats = numericCols.slice(0, 2);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4" data-testid="kpi-tiles">
      <Tile icon={<BarChart3 size={16} />} label="Rows returned" value={formatCompact(rowCount)} />
      {stats.map((s) => (
        <Tile
          key={s.idx}
          icon={<TrendingUp size={16} />}
          label={`${humanize(s.name)} · total`}
          value={formatCompact(s.sum)}
          sub={rows.length > 1 ? `max ${formatCompact(s.max)}` : undefined}
        />
      ))}
    </div>
  );
}

function Tile({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-datumcraft-slate/25 bg-white p-3 shadow-card">
      <div className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-datumcraft-slate">
        <span className="text-datumcraft-accentDark">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 font-display text-xl font-semibold text-datumcraft-navy">{value}</div>
      {sub && <div className="text-2xs text-datumcraft-slate">{sub}</div>}
    </div>
  );
}

function pickIcon(col: string): React.ReactNode {
  const lower = col.toLowerCase();
  if (/pct|percent|rate|ratio/.test(lower)) return <Percent size={16} />;
  if (/count|total|rows|num/.test(lower))   return <Hash size={16} />;
  return <TrendingUp size={16} />;
}

function humanize(col: string): string {
  return col
    .replaceAll("_", " ")
    .replace(/\bms\b/gi, "ms")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
